import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Set, Map, OrderedMap } from 'immutable';
import get from 'lodash/get';
import result from 'lodash/result';
import isEmpty from 'lodash/isEmpty';

import { INPUT_CHANGE, INPUT_IMPRESSION } from 'lib/analytics/events';
import { routerLocationShape, dataCollectorShape, applePayInstanceShape } from 'lib/common/prop-types';
import { formatPhoneNumber } from 'lib/common/format-phone-number';

import User from 'models/user';
import { Location } from 'models/locations';
import Quote from 'models/quote';

import AuthenticatedAccountInformation from 'components/checkout/account-information/authenticated';
import GuestAccountInformation from 'components/checkout/account-information/guest';
import RequiredField from 'components/common/required-field';
import RequiredLicensePlate, { REQUIRED_LICENSE_PLATE } from 'components/checkout/required-license-plate';

// TODO revisit once app merge is complete (immutable backend) to see if
// this is still needed for performance gains
const INPUT_CHANGE_TIMEOUT = 500;

const propTypes = {
  user: PropTypes.instanceOf(User).isRequired,
  onUserInfoChange: PropTypes.func.isRequired,
  userInfo: PropTypes.instanceOf(Map),
  checkoutTotal: PropTypes.number,
  submitCheckout: PropTypes.func.isRequired,
  canSubmit: PropTypes.bool,
  setUserInfo: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  routerLocation: routerLocationShape.isRequired,
  location: PropTypes.instanceOf(Location).isRequired,
  locale: PropTypes.string.isRequired,
  requiredFields: PropTypes.instanceOf(Set),
  requiredFieldErrors: PropTypes.oneOfType([
    PropTypes.instanceOf(Map),
    PropTypes.instanceOf(OrderedMap),
  ]),
  shouldScrollToError: PropTypes.bool,
  setShouldScrollToError: PropTypes.func.isRequired,
  applePayInstance: applePayInstanceShape,
  dataCollector: dataCollectorShape,
  vehicles: PropTypes.instanceOf(OrderedMap),
  addPrepurchaseLicensePlate: PropTypes.func.isRequired,
  selectedQuote: PropTypes.instanceOf(Quote).isRequired,
  selectedLocation: PropTypes.instanceOf(Location).isRequired,
};

const defaultProps = {
  checkoutTotal: null,
  canSubmit: true,
  userInfo: Map(),
  requiredFields: Set(),
  requiredFieldErrors: Map(),
  shouldScrollToError: false,
  dataCollector: null,
  applePayInstance: null,
  vehicles: new OrderedMap(),
};
class AccountInformation extends Component {
  constructor(props) {
    super(props);

    this.onEmailChange = this.onEmailChange.bind(this);
    this.onFirstnameChange = this.onFirstnameChange.bind(this);
    this.onLastnameChange = this.onLastnameChange.bind(this);
    this.onPhoneNumberChange = this.onPhoneNumberChange.bind(this);
    this.onEmailFieldChange = this.onEmailFieldChange.bind(this);
    this.onFirstnameFieldChange = this.onFirstnameFieldChange.bind(this);
    this.onLastnameFieldChange = this.onLastnameFieldChange.bind(this);
    this.onPhoneNumberFieldChange = this.onPhoneNumberFieldChange.bind(this);
    this.onMarketingOptInChange = this.onMarketingOptInChange.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.trackFirstName = this.trackFirstName.bind(this);
    this.trackLastName = this.trackLastName.bind(this);
    this.trackEmail = this.trackEmail.bind(this);
    this.trackPhoneNumber = this.trackPhoneNumber.bind(this);

    // TODO Write a test to verify that this information gets populated on initial
    // guest form render
    const { email = '', firstname = '', lastname = '', phone = '' } = this.props.user || {};
    this.state = { email, firstname, lastname, phone };
  }

  componentWillReceiveProps(nextProps) {
    const { userInfo } = this.props;
    const newState = {};
    if (nextProps.userInfo) {
      if (nextProps.userInfo.get('firstname') !== userInfo.get('firstname')) {
        newState.firstname = nextProps.userInfo.get('firstname');
      }

      if (nextProps.userInfo.get('lastname') !== userInfo.get('lastname')) {
        newState.lastname = nextProps.userInfo.get('lastname');
      }

      if (nextProps.userInfo.get('email') !== userInfo.get('email')) {
        newState.email = nextProps.userInfo.get('email');
      }

      if (nextProps.userInfo.get('phone') !== userInfo.get('phone')) {
        newState.phone = nextProps.userInfo.get('phone');
      }

      if (Object.keys(newState).length > 0) {
        this.setState(newState);
      }
    }
  }

  onEmailChange() {
    this.props.onUserInfoChange({ email: this.state.email });
  }

  onEmailFieldChange(e) {
    clearTimeout(this.emailChangeTimeout);
    const { target } = e;
    this.setState({ email: target.value });
    this.emailChangeTimeout = setTimeout(this.onEmailChange, INPUT_CHANGE_TIMEOUT);
    this.trackEmail(INPUT_CHANGE);
  }

  onFirstnameChange() {
    this.props.onUserInfoChange({ firstname: this.state.firstname });
  }

  onFirstnameFieldChange(e) {
    clearTimeout(this.firstnameChangeTimeout);
    const { target } = e;
    this.setState({ firstname: target.value });
    this.firstnameChangeTimeout = setTimeout(this.onFirstnameChange, INPUT_CHANGE_TIMEOUT);
    this.trackFirstName(INPUT_CHANGE);
  }

  onLastnameChange() {
    this.props.onUserInfoChange({ lastname: this.state.lastname });
  }

  onLastnameFieldChange(e) {
    clearTimeout(this.lastnameChangeTimeout);
    const { target } = e;
    this.setState({ lastname: target.value });
    this.lastnameChangeTimeout = setTimeout(this.onLastnameChange, INPUT_CHANGE_TIMEOUT);
    this.trackLastName(INPUT_CHANGE);
  }

  onPhoneNumberChange() {
    this.props.onUserInfoChange({ phone: this.state.phone });
  }

  onPhoneNumberFieldChange(e) {
    clearTimeout(this.phoneNumberChangeTimeout);
    const { target } = e;
    let newNumber = target.value.replace(/\D/g, '');
    const maxLength = newNumber[0] === '1' ? 11 : 10;

    // Don't let user enter more numbaZ
    newNumber = newNumber.substring(0, maxLength);
    this.setState({ phone: newNumber });
    this.phoneNumberChangeTimeout = setTimeout(this.onPhoneNumberChange, INPUT_CHANGE_TIMEOUT);
    this.trackPhoneNumber(INPUT_CHANGE);
  }

  onMarketingOptInChange(marketingAllowed) {
    this.setState({ marketingAllowed });
    this.props.onUserInfoChange({ marketingAllowed });
  }

  trackEvent({ event, name, required, field, visible }) {
    if (event === INPUT_IMPRESSION && !visible) { return; }

    const { trackEvent } = this.props;

    trackEvent({
      ...event,
      name,
      properties: {
        required: result(required, 'toString', 'false'),
        filled: (!isEmpty(get(field, 'value', ''))).toString(),
      },
    });
  }

  trackFirstName(event, visible = false) {
    this.trackEvent({ event, name: 'FirstName', required: true, field: this.firstnameField, visible });
  }

  trackLastName(event, visible = false) {
    this.trackEvent({ event, name: 'LastName', required: true, field: this.lastnameField, visible });
  }

  trackEmail(event, visible = false) {
    this.trackEvent({ event, name: 'Email', required: true, field: this.emailField, visible });
  }

  trackPhoneNumber(event, visible = false) {
    this.trackEvent({ event, name: 'PhoneNumber', required: false, field: this.phoneField, visible });
  }


  render() {
    return (
      <Fragment>
        <AuthenticatedAccountInformation
          user={this.props.user}
        />
        <GuestAccountInformation
          {...this.props}
          {...this.state}
          parent={this}
          onFirstnameFieldChange={this.onFirstnameFieldChange}
          trackFirstName={this.trackFirstName}
          onLastnameFieldChange={this.onLastnameFieldChange}
          trackLastName={this.trackLastName}
          onEmailFieldChange={this.onEmailFieldChange}
          trackEmail={this.trackEmail}
          onPhoneNumberFieldChange={this.onPhoneNumberFieldChange}
          trackPhoneNumber={this.trackPhoneNumber}
          formatPhoneNumber={formatPhoneNumber}
          onMarketingOptInChange={this.onMarketingOptInChange}
        />
        <RequiredField
          dataFieldName={REQUIRED_LICENSE_PLATE}
          modifyRequiredFieldList={this.props.modifyRequiredFieldList}
          requiredFieldErrors={this.props.requiredFieldErrors}
          requiredFields={this.props.requiredFields}
          shouldScrollToError={this.props.shouldScrollToError}
          setShouldScrollToError={this.props.setShouldScrollToError}
        >
          <RequiredLicensePlate
            {...this.props}
            addPrepurchaseLicensePlate={this.props.addPrepurchaseLicensePlate}
            selectedQuote={this.props.selectedQuote}
            selectedLocation={this.props.selectedLocation}
            vehicles={this.props.vehicles}
          />
        </RequiredField>
      </Fragment>
    );
  }
}

AccountInformation.propTypes = propTypes;
AccountInformation.defaultProps = defaultProps;
export default AccountInformation;
